import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr
} from '@chakra-ui/react'
import { FC, useEffect, useState } from 'react'
import { MdDelete, MdUpload, MdWarning } from 'react-icons/md'

import {
  FeriasCargaRow,
  validateFeriasRow,
  validateFeriasRows
} from '../../../utils/parametrization/feriasCargaCsv'

interface Props {
  isOpen: boolean
  initialRows: FeriasCargaRow[]
  backendErrors?: string[]
  onClose: () => void
  onConfirm: (rows: FeriasCargaRow[]) => Promise<void>
  isLoading?: boolean
}

const FeriasCargaPreviewModal: FC<Props> = ({
  isOpen,
  initialRows,
  backendErrors = [],
  onClose,
  onConfirm,
  isLoading = false
}) => {
  const [rows, setRows] = useState<FeriasCargaRow[]>([])
  const [onlyErrors, setOnlyErrors] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setRows(validateFeriasRows(initialRows))
      setOnlyErrors(false)
    }
  }, [isOpen, initialRows])

  // A cada tecla revalida só a linha editada (campos + conflito com as demais)
  const updateField = (
    lineNumber: number,
    field: keyof FeriasCargaRow,
    value: string
  ) => {
    setRows(prev => {
      const next = prev.map(r =>
        r._lineNumber === lineNumber ? { ...r, [field]: value } : r
      )
      return next.map(r =>
        r._lineNumber === lineNumber ? validateFeriasRow(r, next) : r
      )
    })
  }

  // Ao sair do campo, revalida tudo: a edição pode ter resolvido/criado
  // duplicidade ou sobreposição em outras linhas
  const revalidateAll = () => {
    setRows(prev => validateFeriasRows(prev))
  }

  const handleConfirm = () => {
    if (isLoading) return
    const validated = validateFeriasRows(rows)
    if (validated.some(r => r.errors.length > 0)) {
      setRows(validated)
      return
    }
    onConfirm(validated)
  }

  const removeRow = (lineNumber: number) => {
    setRows(validateFeriasRows(rows.filter(r => r._lineNumber !== lineNumber)))
  }

  const removeAllWithErrors = () => {
    setRows(validateFeriasRows(rows.filter(r => r.errors.length === 0)))
    setOnlyErrors(false)
  }

  const totalErrors = rows.reduce((acc, r) => acc + r.errors.length, 0)
  const hasErrors = totalErrors > 0
  const errorRows = rows.filter(r => r.errors.length > 0)
  const displayedRows = onlyErrors ? errorRows : rows

  const inp = {
    size: 'sm' as const,
    borderRadius: 'md',
    borderColor: 'gray.300',
    _focus: { borderColor: '#0f5238', boxShadow: '0 0 0 1px #0f5238' }
  }
  const errInp = { ...inp, borderColor: 'red.300' }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="full">
      <ModalOverlay />
      <ModalContent
        maxW="1000px"
        mx="auto"
        my={4}
        maxH="calc(100vh - 32px)"
        display="flex"
        flexDirection="column"
        bg="white"
      >
        <ModalHeader
          px={4}
          py={3}
          flexShrink={0}
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          <Flex align="center" gap={3}>
            <Icon as={MdUpload} boxSize={5} color="green.700" />
            <Text fontSize="15px" fontWeight="700">
              Carga de Férias — Preview
            </Text>
            {hasErrors ? (
              <Badge colorScheme="red" borderRadius="full" px={2}>
                {totalErrors} {totalErrors === 1 ? 'erro' : 'erros'}
              </Badge>
            ) : rows.length > 0 ? (
              <Badge colorScheme="green" borderRadius="full" px={2}>
                sem erros
              </Badge>
            ) : null}
          </Flex>
        </ModalHeader>
        <ModalCloseButton top={3} />

        <ModalBody
          bg="white"
          py={3}
          px={4}
          flex={1}
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <Flex align="center" gap={2} mb={2} flexWrap="wrap">
            <Text fontSize="12px" color="gray.500">
              Revise os dados antes de confirmar. Linhas em vermelho possuem
              erros — corrija ou remova antes de enviar.
            </Text>
            {hasErrors && (
              <>
                <Button
                  size="xs"
                  colorScheme="red"
                  variant="outline"
                  onClick={() => setOnlyErrors(f => !f)}
                  flexShrink={0}
                >
                  {onlyErrors
                    ? `Mostrar todos (${rows.length})`
                    : `Filtrar erros (${errorRows.length})`}
                </Button>
                <Button
                  size="xs"
                  colorScheme="red"
                  variant="solid"
                  onClick={removeAllWithErrors}
                  flexShrink={0}
                >
                  Remover todos com erros
                </Button>
              </>
            )}
          </Flex>

          <Box
            flex={1}
            overflow="auto"
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="md"
          >
            <Table size="sm" sx={{ 'td, th': { px: '8px', py: '6px' } }}>
              <Thead bg="gray.100" position="sticky" top={0} zIndex={2}>
                <Tr>
                  <Th fontSize="9px" px={2} py={2} minW="200px">
                    Comprador Backup (e-mail) *
                  </Th>
                  <Th fontSize="9px" px={2} py={2} minW="200px">
                    Comprador Férias (e-mail) *
                  </Th>
                  <Th fontSize="9px" px={2} py={2} minW="110px">
                    Fornecedor Id *
                  </Th>
                  <Th fontSize="9px" px={2} py={2} minW="120px">
                    Data Início *
                  </Th>
                  <Th fontSize="9px" px={2} py={2} minW="120px">
                    Data Fim *
                  </Th>
                  <Th px={2} py={2} w="32px"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {displayedRows.map(row => {
                  const hasRowError = row.errors.length > 0
                  const fieldErr = (field: string) =>
                    row.errors.some(e =>
                      e.toLowerCase().includes(field.toLowerCase())
                    )

                  return (
                    <Tr
                      key={row._lineNumber}
                      bg={hasRowError ? 'red.50' : undefined}
                      _hover={{ bg: hasRowError ? 'red.50' : 'gray.50' }}
                      borderBottom="1px solid"
                      borderColor="gray.100"
                    >
                      <Td>
                        <Flex align="center" gap={1}>
                          <Input
                            {...(fieldErr('principalemail') ? errInp : inp)}
                            onBlur={revalidateAll}
                            value={row.compradorPrincipalEmail}
                            placeholder="email@empresa.com.br"
                            onChange={e =>
                              updateField(
                                row._lineNumber,
                                'compradorPrincipalEmail',
                                e.target.value
                              )
                            }
                          />
                          {hasRowError && (
                            <Tooltip
                              label={
                                <Box>
                                  {row.errors.map((err, i) => (
                                    <Text key={i} fontSize="12px">
                                      • {err}
                                    </Text>
                                  ))}
                                </Box>
                              }
                              hasArrow
                              placement="top"
                              bg="red.700"
                            >
                              <Box cursor="default" flexShrink={0}>
                                <Icon
                                  as={MdWarning}
                                  color="red.500"
                                  boxSize={4}
                                />
                              </Box>
                            </Tooltip>
                          )}
                        </Flex>
                      </Td>
                      <Td>
                        <Input
                          {...(fieldErr('feriasemail') ? errInp : inp)}
                          onBlur={revalidateAll}
                          value={row.compradorFeriasEmail}
                          placeholder="email@empresa.com.br"
                          onChange={e =>
                            updateField(
                              row._lineNumber,
                              'compradorFeriasEmail',
                              e.target.value
                            )
                          }
                        />
                      </Td>
                      <Td>
                        <Input
                          {...(fieldErr('fornecedorid') ? errInp : inp)}
                          onBlur={revalidateAll}
                          value={row.fornecedorId}
                          onChange={e =>
                            updateField(
                              row._lineNumber,
                              'fornecedorId',
                              e.target.value
                            )
                          }
                        />
                      </Td>
                      <Td>
                        <Input
                          {...(fieldErr('início') ? errInp : inp)}
                          onBlur={revalidateAll}
                          type="date"
                          value={row.dataInicio}
                          onChange={e =>
                            updateField(
                              row._lineNumber,
                              'dataInicio',
                              e.target.value
                            )
                          }
                        />
                      </Td>
                      <Td>
                        <Input
                          {...(fieldErr('fim') ? errInp : inp)}
                          onBlur={revalidateAll}
                          type="date"
                          value={row.dataFim}
                          onChange={e =>
                            updateField(
                              row._lineNumber,
                              'dataFim',
                              e.target.value
                            )
                          }
                        />
                      </Td>
                      <Td>
                        <Tooltip label="Remover linha" hasArrow placement="top">
                          <Button
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => removeRow(row._lineNumber)}
                          >
                            <Icon as={MdDelete} boxSize={4} />
                          </Button>
                        </Tooltip>
                      </Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </Box>

          <Flex justify="space-between" align="center" mt={2} flexShrink={0}>
            <Text fontSize="12px" color="gray.500">
              {rows.length} registro{rows.length !== 1 ? 's' : ''}
              {hasErrors && (
                <Text as="span" color="red.500" fontWeight="600">
                  {' '}
                  · {totalErrors} erro{totalErrors !== 1 ? 's' : ''} encontrado
                  {totalErrors !== 1 ? 's' : ''}
                </Text>
              )}
            </Text>
            {hasErrors && (
              <Text fontSize="12px" color="red.500">
                Corrija ou remova todas as linhas com erro para confirmar.
              </Text>
            )}
          </Flex>
        </ModalBody>

        {backendErrors.length > 0 && (
          <Box px={4} pb={2} flexShrink={0}>
            <Text fontSize="12px" fontWeight="600" color="red.600" mb={1}>
              Erros retornados pelo servidor:
            </Text>
            {backendErrors.map((e, i) => (
              <Text key={i} fontSize="12px" color="red.500">
                • {e}
              </Text>
            ))}
          </Box>
        )}

        <ModalFooter bg="white" borderBottomRadius={5} gap={2} flexShrink={0}>
          <Button
            variant="outline"
            borderColor="gray.300"
            color="gray.600"
            onClick={onClose}
            isDisabled={isLoading}
          >
            Cancelar
          </Button>
          <Tooltip
            label={
              hasErrors
                ? 'Resolva todos os erros antes de confirmar'
                : rows.length === 0
                ? 'Nenhum registro para importar'
                : undefined
            }
            isDisabled={!hasErrors && rows.length > 0}
            hasArrow
          >
            <Button
              colorScheme="green"
              leftIcon={<Icon as={MdUpload} />}
              isDisabled={hasErrors || rows.length === 0}
              isLoading={isLoading}
              loadingText="Enviando..."
              onClick={handleConfirm}
            >
              Confirmar Carga
            </Button>
          </Tooltip>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default FeriasCargaPreviewModal
